import {
  ActionReducerMapBuilder,
  AsyncThunk,
  PayloadAction,
  Selector,
  SliceCaseReducers,
  Update,
  ValidateSliceCaseReducers,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";
import { withRefreshFailed } from './refresh-failed';
import { deriveDbFetchStatus, DbFetchStatus } from "./db-fetch-status";
// immer 9.x removed the deep-import path `immer/dist/internal`. The
// public entry only re-exports `Draft`, which is equivalent to the old
// `WritableDraft` for plain object states (`Draft<T>` resolves to a
// writable mutable proxy of T). Aliased here to keep the rest of the
// file readable.
import type { Draft as WritableDraft } from "immer";
import randomstring from "randomstring";
import sift from "sift";
import { sort } from "sift-sort";
import {
  Paginated,
  createEntity,
  createManyEntities,
  deleteEntity,
  findEntity,
  getTotalCount,
  patchEntity,
  updateManyEntities
} from "./backend-api/backend-api";
import { DBThunkHooks, DBThunkInput, DBThunks, DeleteOptions, GenericState, GetStateFn, PatchOptions } from "./DBServiceReducersInterfaces";
import { createSliceQueryCache, serializeQuery } from "./query-cache";
import { BaseEntity } from "@/bt-common/schemas/bt/base-entity";
import { CommonRootState, AppThunkDispatch } from "@common/types/common-root-state-type";
import { logNewlyAddedCampaigns } from "@common/utils/campaign-log-utils";
import { Campaign } from "@common-models/campaign-model";

export const DBserviceAdapter = createEntityAdapter({
  selectId: (item: any) => item?._id ?? item?.tempId ?? null,
});


const publicServices: string[] = []

// Which field decides whether an incoming realtime/refetch entity is "newer"
// than the copy already in the store (the conditional-update freshness guard):
// - 'version'   : strictly-greater `version` wins (default; preserves legacy behavior)
// - 'updatedAt' : strictly-later `updatedAt` wins (for docs that bump updatedAt
//                 per write but carry no `version`, e.g. sessions — whose QR
//                 rotates ~every 20s; a version-only guard silently drops every
//                 post-load update so the displayed QR freezes until a refresh)
// - 'either'    : newer by version OR by updatedAt (robust when version is absent)
// - 'always'    : the incoming copy always wins (for server-owned docs that carry NEITHER
//                 field, e.g. subscriptions — Chargebee-derived rows have no `version` and
//                 no `updatedAt`, so every freshness comparison is 0 > 0 = false and a
//                 refetch after an upgrade/cancel is silently discarded, freezing an open
//                 tab on the plan it first loaded). Only safe where the client never writes
//                 the entity optimistically — there is no local change to clobber.
export type EntityUpdateTrigger = 'version' | 'updatedAt' | 'either' | 'always';

// `updatedAt` is a Mongo timestamp present on persisted docs but not on the
// BaseEntity schema, and different models type it differently (Date, ISO
// string, or a Moment). Read it as `unknown` and normalize to epoch-ms without
// widening to `any`. Returns null when there's no usable timestamp.
const entityUpdatedAtMs = (entity: unknown): number | null => {
  if (entity == null || typeof entity !== 'object' || !('updatedAt' in entity)) {
    return null;
  }
  const v: unknown = entity.updatedAt;
  if (v == null) return null;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'string' || typeof v === 'number') {
    const ms = new Date(v).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  // Moment / Date-like: anything whose valueOf() yields epoch ms.
  if (typeof v === 'object') {
    const ms: unknown = v.valueOf();
    return typeof ms === 'number' && !Number.isNaN(ms) ? ms : null;
  }
  return null;
};

// `createdAt` (like `updatedAt`) is a Mongo timestamp present on persisted docs
// but absent from the BaseEntity schema and typed differently per model. Read it
// defensively as epoch-ms; returns null when there's no usable timestamp (e.g.
// client-side optimistic rows that haven't been persisted yet). Used only by the
// retention/eviction policy below to age-rank entities.
const entityCreatedAtMs = (entity: unknown): number | null => {
  if (entity == null || typeof entity !== 'object' || !('createdAt' in entity)) {
    return null;
  }
  const v: unknown = (entity as { createdAt: unknown }).createdAt;
  if (v == null) return null;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'string' || typeof v === 'number') {
    const ms = new Date(v).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof v === 'object') {
    const ms: unknown = (v as { valueOf(): unknown }).valueOf();
    return typeof ms === 'number' && !Number.isNaN(ms) ? ms : null;
  }
  return null;
};

export const _DBReducerMap: Map<string, any> = new Map();
// `disableOptimisticUpdates` is a UI-only flag the optimistic-update flow
// honors per-call. Lifted into the generic constraint so TS sees it on
// every T without polluting the canonical BaseEntity schema (per "no
// ambiguous shapes" — UI-state never appears on the persisted shape).
export const createDBServiceSlice = <T extends BaseEntity & { disableOptimisticUpdates?: boolean }>({
  service = "",
  initialState,
  hooks,
  customReducers,
  getReducerState,
  customThunks = {},
  updateTrigger = "version",
  maxEntities,
  isEvictionProtected,
}: {
  service: string;
  initialState: GenericState<T>;
  hooks?: DBThunkHooks<T>;
  // Rows this predicate accepts are NEVER evicted by the `maxEntities` cap (#270: a pending row
  // inside the send horizon must stay in the store, or the redux-only send loop silently misses it).
  isEvictionProtected?: (entity: T) => boolean;
  getReducerState: GetStateFn<T>;
  customReducers?:
  | ((builder: ActionReducerMapBuilder<GenericState<T>>) => void)
  | undefined;
  customThunks?: { [key: string]: AsyncThunk<any, any, any> };
  // Per-service freshness guard for conditional updates. Defaults to 'version'
  // to preserve existing behavior for every service that doesn't opt in.
  updateTrigger?: EntityUpdateTrigger;
  // ---------------------------------------------------------------------------
  // CLIENT-SIDE RETENTION CAP (R12-B) — optional per-service bound on how many
  // entities this slice may hold in memory. When unset (the default for every
  // service), behavior is unchanged and there is zero overhead on the hot path.
  //
  // WHY: R12-B's long-session audit found the append-only DB slices — chiefly
  // the message-log services (`message-logs`, `user-messages`) — are the only
  // meaningful unbounded-growth vector in a multi-hour WA session: every sent /
  // received / status-changed message appends a row that is never pruned, so
  // the store (and its per-row selector/sift scans) grows without limit while
  // the tab stays open. Everything else the audit measured is bounded.
  //
  // POLICY: after any growth-path insert (addOne/addMany/upsertOne/upsertMany
  // and the conditional-update variants, which upsert internally), if the id
  // count exceeds `maxEntities` we evict the *oldest* rows back down to the cap,
  // in-place inside the same immer-backed reducer action (no extra dispatch).
  // Oldest is ranked by `createdAt` (epoch-ms), falling back to `_id` string
  // order — a Mongo ObjectId is timestamp-prefixed, so lexicographic `_id`
  // order tracks creation order for persisted rows.
  //
  // WHY OPEN VIEWS CAN'T LOSE VISIBLE ROWS: the cap (10,000) is deliberately set
  // far above any window an open view actually renders. Message-log / activity
  // views are paginated or scoped to a single chat/campaign and show at most a
  // few hundred recent rows; the newest rows are never eviction candidates
  // (eviction only ever removes the *oldest* beyond the cap). So anything
  // evicted is thousands of rows older than the oldest row any current view
  // could be displaying — it is out of every live window in practice. If a
  // deep-history view is ever needed, it re-fetches from the server (the slice
  // is a cache, not the source of truth), so eviction is non-destructive.
  //
  // Eviction goes through the EntityAdapter's `removeMany`, preserving the
  // ids/entities invariants. Because it mutates the slice, the slice reference
  // changes, which correctly invalidates the reference-keyed selectByQuery
  // query-cache (see query-cache.ts) — no separate cache bookkeeping required.
  maxEntities?: number;
}) => {
  const selectors = DBserviceAdapter.getSelectors(getReducerState);
  const CommonRootStateSelectors = DBserviceAdapter.getSelectors();

  const shouldEntityUpdate = (
    state: WritableDraft<GenericState<T>>,
    entity: T
  ): boolean => {
    if (!entity._id) return false;
    const existingEntity = state.entities[entity._id];
    // Brand-new entity always applies (also why a full page refresh, which
    // clears the store, always shows fresh data regardless of trigger).
    if (!existingEntity) return true;

    const newerByVersion =
      (entity.version ?? 0) > (existingEntity.version ?? 0);

    // Newer only when the incoming has a timestamp and it's strictly later
    // (or the existing copy has none yet).
    const incomingMs = entityUpdatedAtMs(entity);
    const existingMs = entityUpdatedAtMs(existingEntity);
    const newerByUpdatedAt =
      incomingMs != null && (existingMs == null || incomingMs > existingMs);

    switch (updateTrigger) {
      case "always":
        return true;
      case "updatedAt":
        return newerByUpdatedAt;
      case "either":
        return newerByVersion || newerByUpdatedAt;
      case "version":
      default:
        return newerByVersion;
    }
  };

  // Retention/eviction (R12-B). No-op unless this service opted in via
  // `maxEntities`. Called at the tail of every growth-path reducer; trims the
  // slice back down to `maxEntities` by removing the oldest rows. Runs O(n log n)
  // ONLY while over the cap (i.e. exactly the runaway case being contained), and
  // the cap bounds n, so the cost is bounded. Mutates the immer draft in place.
  const evictExcess = (state: WritableDraft<GenericState<T>>): void => {
    if (maxEntities === undefined) return;
    const ids = state.ids;
    if (ids.length <= maxEntities) return;

    const excess = ids.length - maxEntities;
    const evictable = isEvictionProtected
      ? ids.filter((id) => { const e = state.entities[id]; return !e || !isEvictionProtected(e as unknown as T); })
      : ids;
    // Age-rank ids ascending (oldest first): by createdAt when both have one,
    // else a row WITH a timestamp is older than one without (persisted rows sort
    // ahead of unpersisted optimistic rows, which are always the newest), else
    // fall back to _id string order (ObjectId hex is creation-time-ordered).
    const ranked = evictable.slice().sort((a, b) => {
      const ta = entityCreatedAtMs(state.entities[a]);
      const tb = entityCreatedAtMs(state.entities[b]);
      if (ta != null && tb != null) {
        if (ta !== tb) return ta - tb;
      } else if (ta != null) {
        return -1;
      } else if (tb != null) {
        return 1;
      }
      const ida = String(a);
      const idb = String(b);
      return ida < idb ? -1 : ida > idb ? 1 : 0;
    });

    DBserviceAdapter.removeMany(state, ranked.slice(0, excess));
  };

  const createSliceFactory = <
    Reducers extends SliceCaseReducers<GenericState<T>>
  >(
    reducers: Reducers &
      ValidateSliceCaseReducers<GenericState<T>, Reducers> = {} as Reducers &
      ValidateSliceCaseReducers<GenericState<T>, Reducers>
  ) => {
    return createSlice({
      name: service,
      initialState,
      reducers: {
        // Growth paths wrapped with evictExcess so the retention cap (when set
        // via maxEntities) is enforced on every insert without an extra
        // dispatch. evictExcess is a no-op for services that didn't opt in.
        addOne: (state, action: PayloadAction<T>) => {
          DBserviceAdapter.addOne(state, action.payload);
          evictExcess(state);
        },
        addMany: (state, action: PayloadAction<T[]>) => {
          DBserviceAdapter.addMany(state, action.payload);
          evictExcess(state);
        },
        setAll: DBserviceAdapter.setAll,
        removeOne: DBserviceAdapter.removeOne,
        removeMany: DBserviceAdapter.removeMany,
        removeAll: DBserviceAdapter.removeAll,
        updateOne: DBserviceAdapter.updateOne,
        updateMany: DBserviceAdapter.updateMany,
        upsertOne: (state, action: PayloadAction<T>) => {
          DBserviceAdapter.upsertOne(state, action.payload);
          evictExcess(state);
        },
        upsertMany: (state, action: PayloadAction<T[]>) => {
          DBserviceAdapter.upsertMany(state, action.payload);
          evictExcess(state);
        },
        clearState: (state) => {
          DBserviceAdapter.removeAll(state);
          state.itemsLoaded = false;
          state.itemsLoadedLocaly = false;
        },
        setItemsLoadedLocaly: (state, action: PayloadAction<boolean>) => {
          state.itemsLoadedLocaly = action.payload;
        },

        conditionalUpdateOne: (state, action: PayloadAction<T>) => {
          const entity = action.payload;
          if (shouldEntityUpdate(state, entity)) {
            DBserviceAdapter.upsertOne(state, entity);
            // upsert can insert a brand-new row → apply the retention cap.
            evictExcess(state);
          }
        },
        conditionalUpdateMany: (state, action: PayloadAction<T[]>) => {
          DBserviceAdapter.updateMany(
            state,
            action.payload
              .filter((entity) => shouldEntityUpdate(state, entity))
              .map((entity) => ({
                id: entity._id!,
                changes: entity,
              }))
          );
        },
        setItemsLoaded: (state, action: PayloadAction<boolean>) => {
          console.log(`[${service}] setItemsLoaded`);
          state.itemsLoaded = action.payload;
        },
        setItemsLoading: (state, action: PayloadAction<boolean>) => {
          state.itemsLoading = action.payload;
        },
        // FE#170: record (or clear, with null) the last fetch error so a failed
        // fetch is distinguishable from an in-flight one.
        setItemsError: (state, action: PayloadAction<string | null>) => {
          state.itemsError = action.payload;
        },
        setItemsTotalSkipAndLimit: (state, action: PayloadAction<{ total: number, skip: number, limit: number }>) => {

          const { total, skip, limit } = action.payload;
          state.total = total;
          state.skip = skip;
          state.limit = limit;

        },
        setItemsTotal: (state, action: PayloadAction<number>) => {
          state.total = action.payload;
        },
        ...customReducers,
      },
      extraReducers: customReducers,
    });
  };
  const slice = createSliceFactory();

  const checkAuthAndService = (
    service: string,
    operation: string,
    thunkAPI: any
  ): boolean => {
    // chatty per WA event; gate behind localStorage.BT_DB_DIAG=1
    if (typeof localStorage !== 'undefined' && localStorage.getItem('BT_DB_DIAG') === '1') {
      console.log(`[${service} ${operation}] triggered`);
    }
    const state = thunkAPI.getState() as CommonRootState;
    if (!publicServices.includes(service) && !state.UserReducer.userId) {
      console.log(`[${service}] Not a public service and user not connected`);
      return false;
    }
    return true;
  };

  // Uses the outer-scope T from createDBServiceSlice (no inner generic
   // — that would shadow + break the hook-return type compatibility).
  const createDBThunks = (service: string): DBThunks<T> => {
    return {
      find: createAsyncThunk(
        `${service}/find`,
        async (query: any, thunkAPI) => {
          if (!checkAuthAndService(service, 'find', thunkAPI)) {
            return;
          }

          const dispatch = thunkAPI.dispatch as AppThunkDispatch;

          // Extract options from query if present
          const { cleanStateBeforeFetch, cleanStateIfResultIsEmpty, useCache } = query

          // Clean state before fetch if option is set to true
          if (cleanStateBeforeFetch) {
            dispatch(slice.actions.clearState());
            delete query.cleanStateBeforeFetch;
          }

          // Remove useCache from query as it's an internal option
          if ('useCache' in query) {
            delete query.useCache;
          }

          dispatch(slice.actions.setItemsLoading(true));
          dispatch(slice.actions.setItemsError(null)); // FE#170: clear any prior fetch error on a new fetch

          // Transform query to handle $in operator
          const transformQuery = (query: any): any => {
            const transformed = { ...query };
            Object.entries(query).forEach(([key, value]) => {
              if (value && typeof value === 'object' && '$in' in value) {
                // Convert {field: {$in: [1,2,3]}} to {field[$in]: []} format
                delete transformed[key];
                // Create array notation that will be properly serialized
                transformed[`${key}[$in][]`] = value.$in;
              }
            });
            return transformed;
          };

          const transformedQuery = transformQuery(query);

          // Default (useCache falsy): force a fresh network round-trip
          // instead of serving RTK Query's in-memory arg cache. This
          // replaces the old `breakCache=<random>` query param, which made
          // every request URL unique and defeated HTTP-level caching / ETag
          // revalidation. `forceRefetch` bypasses only RTK Query's store
          // cache; the browser HTTP cache still applies, so unchanged data
          // now revalidates to a 304 (empty body) against the backend's
          // `Cache-Control: private, no-cache` + ETag instead of refetching
          // the full JSON. useCache:true keeps RTK Query's cached/deduped result.
          // NOTE: must keep the default subscription during the request —
          // `subscribe: false` + keepUnusedDataFor:1 lets RTK Query sweep the
          // cache entry while a >1s request is still in flight, dropping the
          // result (all finds resolved empty). We unsubscribe after consuming.
          const findSubscription = dispatch(
            findEntity.initiate(
              {
                service,
                query: transformedQuery,
              },
              useCache ? undefined : { forceRefetch: true },
            ));
          const getEntityPromise = findSubscription
            .unwrap()
            .then(async (response: Paginated<T> | T[] | undefined) => {
              const data = (response && "data" in response) ? response.data : response;
              const entityList =
                (await hooks?.after?.find?.apply(null, [
                  data ?? [],
                  {
                    dispatch,
                  },
                ])) ?? data ?? [];

              if (cleanStateIfResultIsEmpty && entityList.length === 0) {
                dispatch(slice.actions.clearState());
                delete query.cleanStateIfResultIsEmpty;
              }

              // Log newly added campaigns if this is the campaigns service
              if (service === 'campaigns' && entityList.length > 0) {
                try {
                  const currentState = getReducerState(thunkAPI.getState());
                  // Get MQTT client ID and type if available
                  let clientId: string | null = null;
                  let clientType: string | null = null;
                  try {
                    const { MQTTSubscriber } = await import(/* webpackMode: "eager" */ '@common/noifier-module/MQTTSubscriber');
                    const mqttSubscriber = MQTTSubscriber.getInstance();
                    clientId = mqttSubscriber.getCurrentClientId();
                    clientType = mqttSubscriber.getCurrentClientType();
                  } catch (error) {
                    // MQTT not available, continue without client ID/type
                  }
                  await logNewlyAddedCampaigns(
                    entityList as Campaign[],
                    currentState as GenericState<Campaign>,
                    dispatch,
                    clientId,
                    clientType
                  );
                } catch (error) {
                  console.error('Error logging newly added campaigns in find thunk:', error);
                  // Don't fail the operation if logging fails
                }
              }

              dispatch(slice.actions.addMany(entityList));
              // Refresh existing entities, but only when the fetched copy is
              // newer (higher version). A find can race with an in-flight write
              // (or read a lagging replica) and return a stale snapshot; an
              // unconditional updateMany here would clobber a just-applied
              // optimistic change — e.g. revert a template the user just
              // deleted, making it reappear in the list. conditionalUpdateMany
              // honors the version guard (shouldEntityUpdate).
              dispatch(slice.actions.conditionalUpdateMany(entityList
                .filter((entity: T) => entity._id !== undefined)));
              dispatch(slice.actions.setItemsLoaded(true));

              if (response && "skip" in response && "limit" in response && "total" in response) {
                const { skip, limit, total } = response;
                dispatch(slice.actions.setItemsTotalSkipAndLimit({ skip, limit, total }));
              }

              dispatch(slice.actions.setItemsLoading(false));

              // #297 — surface the backend's `refreshFailed` flag (forced wa-contacts-cache refresh that got a
              // failed/partial pull) on a COPY of the list; the cache arrays are frozen.
              return withRefreshFailed(entityList as T[], response);
            })
            .catch((e) => {
              console.error('Error:', JSON.stringify(e, null, 2));
              // FE#170: record the failure so it is not indistinguishable from
              // "still loading". itemsLoaded stays false; itemsError carries the reason.
              const message = (e && (e.message || e.error || e.name)) || 'Fetch failed';
              dispatch(slice.actions.setItemsError(String(message)));
              dispatch(slice.actions.setItemsLoading(false));
            })
            .finally(() => {
              // Release the RTK Query cache entry once the result has been
              // copied into this slice (keepUnusedDataFor sweeps it shortly
              // after). useCache callers deliberately KEEP their subscription —
              // that retained entry is what serves their next identical find
              // from cache (pre-existing behavior).
              if (!useCache) findSubscription.unsubscribe?.();
            });
          return getEntityPromise;
        }
      ),
      getTotalCount: createAsyncThunk(
        `${service}/getTotalCount`,
        async (_, thunkAPI) => {
          if (!checkAuthAndService(service, 'getTotalCount', thunkAPI)) {
            return;
          }

          const dispatch =
            thunkAPI.dispatch as AppThunkDispatch;

          dispatch(slice.actions.setItemsLoading(true));

          try {
            // Use RTK Query to fetch the total count


            let totalCount = await dispatch(
              getTotalCount.initiate({ service })
            ).unwrap();

            console.log(`[${service}] total count: ${totalCount}`);

            totalCount = Number(totalCount) ?? 0;

            dispatch(slice.actions.setItemsTotal(totalCount ?? 0));
          } catch (e) {
            console.error(e);
          } finally {
            dispatch(slice.actions.setItemsLoading(false));
          }
        }
      ),
      create: createAsyncThunk(
        `${service}/create2`,
        async (entity: T, thunkAPI) => {

          if (!checkAuthAndService(service, 'create2', thunkAPI)) {
            return;
          }


          entity.version = 1;
          const dirtySessionId = randomstring.generate(16);
          entity = { ...entity, dirtySessionId, dirty: true };

          const dispatch =
            thunkAPI.dispatch as AppThunkDispatch;

          if (hooks?.before?.create) {
            entity = await hooks?.before?.create(entity, { dispatch });
          }

          if (!entity?.disableOptimisticUpdates) {
            dispatch(
              slice.actions.addOne({
                ...entity,
                _id: dirtySessionId,
                tempId: dirtySessionId,
                deleted: false,
              })
            );
          }

          let response;
          try {
            let newEntity = await dispatch(
              createEntity.initiate({
                service,
                entity,
              })
            ).unwrap();

            newEntity = {
              ...newEntity,
              dirtySessionId: undefined,
              dirty: false,
            };

            if (hooks?.after?.create) {
              newEntity = await hooks?.after?.create(newEntity, { dispatch });
            }

            if (!entity?.disableOptimisticUpdates) {
              dispatch(
                slice.actions.updateOne({
                  id: dirtySessionId,
                  changes: newEntity,
                })
              );
            }
            else {
              dispatch(
                slice.actions.addOne({
                  id: newEntity._id,
                  ...newEntity,
                })
              )
            }


            response = newEntity;
          } catch (e) {
            console.error(e);
            dispatch(slice.actions.removeOne(dirtySessionId));

            response = e;
          }
          return response;
        }
      ),
      patch: createAsyncThunk<T, DBThunkInput<Partial<T>, PatchOptions>>(
        `${service}/patch2`,
        async ({ entity: entityArg, options }: DBThunkInput<Partial<T>, PatchOptions>, thunkAPI) => {
          let entity = entityArg as T;


          if (!checkAuthAndService(service, 'patch2', thunkAPI)) {
            return;
          }

          const dispatch =
            thunkAPI.dispatch as AppThunkDispatch;

          //const localState = getReducerState(getState());
          if (!entity._id) {
            console.log(`patch entity must have _id`);
            return;
          }
          const originalEntity = selectors.selectById(
            thunkAPI.getState(),
            entity._id
          );
          if (!options?.ignoreOriginalEntity && !originalEntity) {
            console.log(`patch didnt found the entity - entity._id : ${entity._id}  not exists`)
            return;
          }
          if (!options?.ignoreOriginalEntity && !originalEntity._id) {
            console.log(`patch originalEntity didnt have _id. originalEntity:`)
            console.log(originalEntity)
            return;
          }

          const dirtySessionId = randomstring.generate(16);
          entity = {
            ...entity,
            dirtySessionId,
            dirty: true,
            version: (originalEntity?.version ?? 0) + 1,
          };

          if (hooks?.before?.patch) {
            entity = await hooks?.before?.patch(entity, { dispatch });
          }

          if (!options?.disableOptimisticUpdates && entity._id) {
            // dev-leftover log — fires on every patch, value is rarely set
            dispatch(
              slice.actions.updateOne({
                id: entity._id,
                changes: entity,
              })
            );
          }

          let newEntity
          try {
            newEntity = await dispatch(
              patchEntity.initiate({
                service,
                entity,
              })
            ).unwrap();

            newEntity = {
              ...newEntity,
              dirtySessionId: undefined,
              dirty: false,
            };
            if (hooks?.after?.patch) {
              newEntity = await hooks.after.patch(newEntity, { dispatch });
            }
            if (options?.disableOptimisticUpdates && newEntity?._id) {
              dispatch(slice.actions.updateOne({
                id: newEntity._id,
                changes: newEntity
              }));
            } else {
              dispatch(slice.actions.conditionalUpdateOne(newEntity));
            }
          } catch (e) {

            if (options?.inCaseOfErrorRollbackOptimisticUpdateDisabled && originalEntity?._id) {
              dispatch(
                slice.actions.updateOne({
                  id: originalEntity._id,
                  changes: originalEntity,
                })
              );
            }


            const errData = (e instanceof Error ? undefined : (e as { data?: unknown })?.data) ?? e;
            thunkAPI.rejectWithValue(errData);
            return errData;
          }
          return newEntity;
        }
      ),
      patchMany: createAsyncThunk<void, Update<T>[]>(
        `${service}/patchMany`,
        async (updates: Update<T>[], thunkAPI) => {

          if (!checkAuthAndService(service, 'patchMany', thunkAPI)) {
            return;
          }


          if (updates.length === 0) {
            return;
          }

          const dispatch = thunkAPI.dispatch as AppThunkDispatch;
          const localState = getReducerState(thunkAPI.getState());

          const ids = updates.map(update => update.id)
          const projection = Object.keys(updates[0]?.changes);

          const originalEntities = selectByQuery(localState,
            {
              _id: { $in: ids }
            },
            projection,
            undefined
          );

          if (originalEntities.length !== ids.length) {
            console.log(`patchMany didnt found the entities - some of them may not exists`)
            return;
          }

          dispatch(slice.actions.updateMany(updates));

          const deletedEntitiesPromise = dispatch(updateManyEntities.initiate(
            { service, entities: updates })).unwrap()
            .then(async (response: any) => {

              const entities = response?.map((entity: T) => {
                return {
                  ...entity,
                  dirtySessionId: undefined,
                  dirty: false,
                }
              })
              dispatch(slice.actions.conditionalUpdateMany(entities));
              return entities;
            }).catch((e) => {
              console.error(e);
              const beforeChanges = originalEntities
                .filter((entity): entity is T & { _id: string } => entity._id !== undefined)
                .map(entity => {
                  return {
                    id: entity._id,
                    changes: entity
                  }
                })
              dispatch(slice.actions.updateMany(beforeChanges));
            });

          return deletedEntitiesPromise;
        }
      ),
      createMany: createAsyncThunk<any[], T[]>(
        `${service}/createMany`,
        async (entities: T[], thunkAPI) => {
          if (!checkAuthAndService(service, 'createMany', thunkAPI)) {
            return [];
          }

          const dispatch = thunkAPI.dispatch as AppThunkDispatch;

          // Initialize responses array
          let responses: any[] = [];

          // Prepare entities with initial modifications and optimistic IDs
          const preparedEntities = entities.map(entity => {
            entity.version = 1;
            const dirtySessionId = randomstring.generate(16);
            return { ...entity, dirtySessionId, dirty: true, tempId: dirtySessionId };
          });

          // Run hooks before create if any
          const beforeCreate = hooks?.before?.create;
          const entitiesAfterBeforeHook = beforeCreate
            ? await Promise.all(preparedEntities.map(entity => beforeCreate(entity, { dispatch })))
            : preparedEntities;

          // Optimistically add entities to the store
          dispatch(slice.actions.addMany(entitiesAfterBeforeHook.map(entity => ({
            ...entity,
            _id: entity.dirtySessionId,
          }))));

          try {
            // Attempt to create multiple entities
            const newEntities = await dispatch(
              createManyEntities.initiate({
                service,
                entities: entitiesAfterBeforeHook,
              })
            ).unwrap();

            // Post creation updates or hooks
            const afterCreate = hooks?.after?.create;
            const entitiesAfterAfterHook = afterCreate
              ? await Promise.all(newEntities.map(entity => afterCreate(entity, { dispatch })))
              : newEntities;

            // Swap the optimistic rows for the server's. We CANNOT pair them via the
            // echoed `dirtySessionId`: the bulk insert goes through the raw model, whose
            // schema drops that field (and `tempId`), so the echo carries neither — an
            // `updateMany({id: undefined})` is a no-op, the temp rows survive, and the
            // real rows arriving over realtime/refetch are counted ON TOP of them
            // (5 imported contacts → 10 in the audience count). Drop every optimistic
            // row we added, then upsert the real ones (idempotent vs the realtime echo).
            dispatch(slice.actions.removeMany(
              entitiesAfterBeforeHook.flatMap(entity => (entity.dirtySessionId ? [entity.dirtySessionId] : []))
            ));
            dispatch(slice.actions.upsertMany(entitiesAfterAfterHook.map(entity => ({
              ...entity,
              dirtySessionId: undefined,
              dirty: false,
            }))));
          } catch (e) {
            console.error(e);

            // Rollback optimistic updates by removing all entities added
            entitiesAfterBeforeHook.forEach(entity => {
              if (entity.dirtySessionId) dispatch(slice.actions.removeOne(entity.dirtySessionId));
            });

            // Push error to responses
            responses.push(e);
          }

          return responses;
        }
      ),
      delete: createAsyncThunk<T, DBThunkInput<Partial<T>, DeleteOptions>>(
        `${service}/delete2`,
        async ({ entity: entityArg, options }: DBThunkInput<Partial<T>, DeleteOptions>, thunkAPI) => {
          let entity = entityArg as T;

          if (!checkAuthAndService(service, 'delete2', thunkAPI)) {
            return;
          }

          const dispatch =
            thunkAPI.dispatch as AppThunkDispatch;

          if (!entity._id) {
            console.log(`patch entity must have _id`);
            return;
          }
          const originalEntity = selectors.selectById(
            thunkAPI.getState(),
            entity._id
          );
          const dirtySessionId = randomstring.generate(16);
          entity = {
            _id: entity._id,
            dirtySessionId,
            dirty: true,
            deleted: true,
            version: (originalEntity?.version ?? 0) + 1,
          } as any;

          if (hooks?.before?.delete) {
            entity = await hooks?.before?.delete(entity, { dispatch });
          }

          if (entity._id) dispatch(slice.actions.removeOne(entity._id));

          const deletedEntityPromise = dispatch(
            deleteEntity.initiate({
              service,
              entity,
            })
          ).unwrap();
          deletedEntityPromise
            .then(async (newEntity: T) => {
              newEntity = {
                ...newEntity,
                dirtySessionId: undefined,
                dirty: false,
              };
              if (hooks?.after?.delete) {
                newEntity = await hooks.after.delete(newEntity, { dispatch });
              }
              if (newEntity._id) dispatch(slice.actions.removeOne(newEntity._id));
            })
            .catch((e) => {
              // When an entity is not found, it means it was already removed so don't return it to the entity map

              console.log(entity);
              if (!(e?.data?.name === "NotFound" || options?.inCaseOfErrorRollbackOptimisticUpdateDisabled) && originalEntity) {
                dispatch(slice.actions.addOne(originalEntity));
              }
              console.error(e);
            });

          return deletedEntityPromise;
        }
      ),
      createOptimisticUpdate: createAsyncThunk<T, T>(
        `${service}/createOptimisticUpdate`,
        async (entity: T, thunkAPI) => {

          if (!checkAuthAndService(service, 'createOptimisticUpdate', thunkAPI)) {
            return entity;
          }

          const dirtySessionId = randomstring.generate(16);
          entity = { ...entity, dirtySessionId, dirty: true };

          const dispatch = thunkAPI.dispatch as AppThunkDispatch;
          const dirtyEntity = {
            ...entity,
            _id: dirtySessionId,
            tempId: dirtySessionId,
          };

          if (entity._id) {
            dispatch(slice.actions.updateOne({
              id: entity._id,
              changes: dirtyEntity
            }));
          } else {
            dispatch(slice.actions.addOne({ dirtyEntity }));
          }
          return entity;
        }
      ),
      syncOptimisticUpdate: createAsyncThunk<T, T>(
        `${service}/syncOptimisticUpdate`,
        async (newEntity: T, thunkAPI) => {
          if (!checkAuthAndService(service, 'syncOptimisticUpdate', thunkAPI)) {
            return newEntity;
          }

          const dirtySessionId = newEntity.dirtySessionId
          newEntity = {
            ...newEntity,
            dirtySessionId: undefined,
            dirty: false,
          };

          const dispatch = thunkAPI.dispatch as AppThunkDispatch;

          // Remove the dirty entity using its dirtySessionId
          if (dirtySessionId) dispatch(slice.actions.removeOne(dirtySessionId));

          // Insert the newEntity using its actual _id
          dispatch(slice.actions.addOne(newEntity));
          return newEntity;
        }
      ),
      undoOptimisticUpdate: createAsyncThunk<string, string>(
        `${service}/undoOptimisticUpdate`,
        async (dirtySessionId: string, thunkAPI) => {
          if (!checkAuthAndService(service, 'undoOptimisticUpdate', thunkAPI)) {
            return dirtySessionId;
          }
          const dispatch = thunkAPI.dispatch as AppThunkDispatch;

          dispatch(slice.actions.removeOne(dirtySessionId));
          return dirtySessionId;
        }
      ),
      ...customThunks,
    };
  };

  const dbThunks: DBThunks<T> = createDBThunks(service);

  type DBReducerState = GenericState<T>;

  const stateSelector: Selector<DBReducerState, DBReducerState> = (
    state: DBReducerState
  ) => state;



  const getFilterObj = (_: DBReducerState, filterObj: unknown) => {
    return filterObj
  };

  const getProjection = (_: DBReducerState, __: unknown, projectionArray: unknown) => {
    return projectionArray
  };

  const getProjectionField = (_: DBReducerState, __: unknown, projectionField: unknown) => {
    return projectionField
  };

  const getSortObj = (_: DBReducerState, __: unknown, ___: unknown, sortObj: unknown) => {
    return sortObj
  };

  // Per-entity, per-serialized-query result cache (bounded LRU). Keyed on the RTK
  // slice reference so it invalidates precisely when (and only when) the slice mutates.
  // See query-cache.ts for the correctness/bypass rationale.
  const queryCache = createSliceQueryCache<DBReducerState, T[]>();

  const _selectByQuery = createSelector(
    [stateSelector, getFilterObj, getProjection, getSortObj],
    (state: DBReducerState, filterObjArg: unknown, projectionArrayArg: unknown, sortObjArg: unknown = {}): T[] => {
      const filterObj = (filterObjArg ?? {}) as Record<string, unknown>;
      const projectionArray = Array.isArray(projectionArrayArg) ? (projectionArrayArg as string[]) : undefined;
      const sortObj = (sortObjArg ?? {}) as Record<string, unknown>;
    if (!filterObj || Object.keys(filterObj).length === 0) {
      //console.log(`filterObj param ${service} is empty`);
      return CommonRootStateSelectors.selectAll(state);
    }
    if (!state) {
      //console.log(`state param ${service} is undefined`);
      return [];
    }
    if (!state.ids || state.ids.length === 0) {
      //console.log(`state.ids ${service} is empty`);
      return [];
    }

    let resultsArray: T[] = [];

    const siftFunction = sift(filterObj);
    resultsArray = (state.ids ?? [])
      .filter(id => siftFunction(state.entities[id]))
      .map(id => state.entities[id]) as T[];

    if (Object.keys(sortObj).length !== 0) {
      resultsArray = sort(resultsArray, sortObj as Record<string, number>);
    }

    if (projectionArray && projectionArray.length > 0) {
      resultsArray = resultsArray.map((entity: T | {}) => {
        const src = entity as Record<string, unknown>;
        const projected: Record<string, unknown> = projectionArray.reduce<Record<string, unknown>>((a, e) => { a[e] = src[e]; return a; }, {})
        return projected;
      }) as T[];
    }

    return resultsArray;

  });

  const selectByQuery = (
    state: DBReducerState,
    filterObj: unknown,
    projectionArray?: unknown,
    sortObj?: unknown,
  ): T[] => {
    // Fast path: an exact single-key {_id: <string>} filter is a direct entity-map lookup —
    // avoid the full sift() scan over all ids (O(n) per call).
    const idOnlyFilter = filterObj as Record<string, unknown> | null | undefined;
    if (
      idOnlyFilter &&
      typeof idOnlyFilter._id === 'string' &&
      Object.keys(idOnlyFilter).length === 1 &&
      (!Array.isArray(projectionArray) || projectionArray.length === 0)
    ) {
      const entityById = state?.entities?.[idOnlyFilter._id as string];
      return entityById !== undefined && (entityById as any)?._id === idOnlyFilter._id ? [entityById as T] : [];
    }

    // Per-(slice, query) memoization: the shared reselect selector below has a
    // cache size of 1 and reference-based input equality, so the inline-literal
    // queries at ~109 call sites never hit it — the full sift() scan re-runs on
    // every dispatch per subscriber and yields a fresh array. Here we serialize
    // the query and, when the slice reference is unchanged, return the previously
    // computed array (same reference — no re-scan). Semantically identical: same
    // immutable slice + same query ⇒ identical result. Unsafe-to-serialize
    // queries (functions/RegExp) get `null` and fall through to the reselect path.
    if (state) {
      const key = serializeQuery(
        filterObj ?? {},
        Array.isArray(projectionArray) ? projectionArray : null,
        sortObj ?? {},
      );
      if (key !== null) {
        const cached = queryCache.get(key, state);
        if (cached !== undefined) return cached;
        const result = _selectByQuery(state, filterObj, projectionArray, sortObj);
        queryCache.set(key, state, result);
        return result;
      }
    }

    return _selectByQuery(state, filterObj, projectionArray, sortObj);
  };

  const selectByQueryOnlyLength = createSelector(
    [stateSelector, getFilterObj],
    (state: DBReducerState, filterObj: unknown): number => {
      return selectByQuery(state, filterObj, undefined, undefined).length
    });

  const selectOneObjectById = createSelector(
    [stateSelector, getFilterObj],
    (state: DBReducerState, idArg: unknown): T | undefined => {
      const id = idArg as string;
      const object = CommonRootStateSelectors.selectById(state, id);
      return !object?.deleted ? object : undefined;
    });

  const selectOneFieldById = createSelector(
    [stateSelector, getFilterObj, getProjectionField],
    (state: DBReducerState, idArg: unknown, projectionFieldArg: unknown): unknown => {
      const id = idArg as string;
      const projectionField = projectionFieldArg as string | undefined;
      if (!projectionField) {
        return null;
      }

      const onlyOne = selectOneObjectById(state, id);

      if (!onlyOne) {
        return null;
      }

      if (Object.prototype.hasOwnProperty.call(onlyOne, projectionField)) {
        return onlyOne[projectionField as keyof T];
      }

      return null;
    });


  const _selectOneObjectByQuery = createSelector(
    [stateSelector, getFilterObj, getProjection, getSortObj],
    (state: DBReducerState, filterObj: unknown, projectionArray: unknown, sortObj: unknown = {}): T | undefined => {
      let queryResult = selectByQuery(state, filterObj, projectionArray, sortObj);

      if (queryResult.length >= 1) {
        const onlyOne: T = queryResult[0];
        return onlyOne;
      }

      return undefined;
    });

  const selectOneObjectByQuery = (
    state: DBReducerState,
    filterObj: unknown,
    projectionArray?: unknown,
    sortObj?: unknown,
  ): T | undefined => _selectOneObjectByQuery(state, filterObj, projectionArray, sortObj);


  const _selectOneFieldByQuery = createSelector(
    [stateSelector, getFilterObj, getProjectionField, getSortObj],
    (state: DBReducerState, filterObj: unknown, projectionFieldArg: unknown, sortObj: unknown = {}): unknown => {
      const projectionField = projectionFieldArg as string | undefined;
      if (!projectionField) {
        return null;
      }

      const onlyOne = selectOneObjectByQuery(state, filterObj, [projectionField], sortObj);

      if (!onlyOne) {
        return null;
      }

      if (Object.prototype.hasOwnProperty.call(onlyOne, projectionField)) {
        return (onlyOne as Record<string, unknown>)[projectionField];
      }

      return null;
    });

  const selectOneFieldByQuery = (
    state: DBReducerState,
    filterObj: unknown,
    projectionField?: unknown,
    sortObj?: unknown,
  ): unknown => _selectOneFieldByQuery(state, filterObj, projectionField, sortObj);


  const getField = (_: DBReducerState, __: unknown, projection: unknown) => {
    return projection
  };

  const getOper = (_: DBReducerState, __: unknown, ___: unknown, sortObj: unknown) => {
    return sortObj
  };

  const selectByQueryOnlyMaxMin =
    createSelector(
      [stateSelector, getFilterObj, getField, getOper],
      (state: DBReducerState, filterObj: unknown, _fieldArg: unknown, _operArg: unknown): number | null => {

          const field = "order";
          const oper = "max";

          const array = selectByQuery(state, filterObj, undefined, undefined)

          if (array.length === 0)
            return null;

          const mathFn = window.Math[oper as 'max' | 'min'];
          const result = mathFn(...array.map((o: T) => Number((o as Record<string, unknown>)[field])))

          return result

        });

  // create selectPaginationStatus selector that returns the pagination status of the current slice
  const selectPaginationStatus = createSelector<
    [Selector<DBReducerState, DBReducerState>],
    { itemsLoaded: boolean, itemsLoading: boolean, itemsError: string | null, fetchStatus: DbFetchStatus, total: number, skip: number, limit: number }
  >([stateSelector], (state: DBReducerState) => {
    // FE#170: surface itemsError + a single derived fetchStatus so consumers can
    // distinguish 'error' from 'loading' (previously both read as "not loaded").
    return {
      itemsLoaded: state.itemsLoaded,
      itemsLoading: state.itemsLoading,
      itemsError: state.itemsError ?? null,
      fetchStatus: deriveDbFetchStatus(state),
      total: state.total,
      skip: state.skip,
      limit: state.limit
    }
  }
  );



  const dbReducer = {
    slice,
    dbThunks,
    selectByQuery,
    selectors,
    selectByQueryOnlyLength,
    selectByQueryOnlyMaxMin,
    selectOneObjectByQuery,
    selectOneFieldByQuery,
    selectPaginationStatus,
    selectOneObjectById,
    selectOneFieldById,
    getReducerState
  };
  _DBReducerMap.set(service, dbReducer);

  return dbReducer;
};


